import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { AppType } from "types/appType";
import { ErrorType } from "types/errorType";
import { OtherScenarioType, VORScenarioType } from "types/scenarioTypes";
import { ExtractErrorInfo } from "components/shared/ExtractErrorInfo";
import { API_ENDPOINTS } from "Config/apiConfig";
import { api } from "Config/Api";
import { roundTimeToInterval } from "utils/formatDateTime";
import extractIdFromLocation from "utils/extractIdFromLocation";
import { Status } from "Config/Enum/statusEnum";

export interface AppTypeWithScenarioType extends AppType {
  loading: boolean;
  error: ErrorType | null;
  vorScenario: VORScenarioType;
  otherScenario: OtherScenarioType;
}

const initialState: AppTypeWithScenarioType = {
  error: null,
  loading: false,
  vorScenario: {
    id: "",
    externalReference: "",
    incidentReason: "",
    location: "",
    incidentDateTime: roundTimeToInterval(undefined, 5, true),
    estimatedCompletionDateTime: null,
    category: {
      id: "",
      name: "",
      code: "",
    },
    status: "",
    odometerReading: {
      unit: "",
      value: 0,
    },
  },
  otherScenario: {
    id: "",
    externalReference: "",
    incidentReason: "",
    location: "",
    additionalInformation: "",
    odometerReading: {
      unit: "",
      value: null,
    },
    category: {
      id: "",
      name: "",
      code: "",
    },
    status: Status.OPEN,
  },
};

export const createVorScenario = createAsyncThunk<
  VORScenarioType,
  { incidentId: string; vorScenario: VORScenarioType }
>("vorScenario/createVorScenario", async ({ incidentId, vorScenario }, { rejectWithValue }) => {
  try {
    const response = await api.post<VORScenarioType>(API_ENDPOINTS.addVORScenario(incidentId), vorScenario);
    const locationHeader = response.headers["location"] || response.headers["Location"];
    if (!locationHeader) throw new Error("Missing Location header from createVorScenario response");

    return {
      ...vorScenario,
      id: extractIdFromLocation(locationHeader),
    } as VORScenarioType;
  } catch (error: any) {
    return rejectWithValue(ExtractErrorInfo(error));
  }
});

export const updateVorScenario = createAsyncThunk<
  VORScenarioType,
  { incidentId: string; vorScenario: VORScenarioType }
>("vorScenario/updateVorScenario", async ({ incidentId, vorScenario }, { rejectWithValue }) => {
  try {
    const response = await api.patch<VORScenarioType>(
      API_ENDPOINTS.updateVORScenario(incidentId, vorScenario.id!),
      vorScenario
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(ExtractErrorInfo(error));
  }
});

export const createOtherScenario = createAsyncThunk<
  OtherScenarioType,
  { incidentId: string; otherScenario: OtherScenarioType }
>("otherScenario/createOtherScenario", async ({ incidentId, otherScenario }, { rejectWithValue }) => {
  try {
    const response = await api.post<OtherScenarioType>(API_ENDPOINTS.addOtherScenario(incidentId), otherScenario);
    const locationHeader = response.headers["location"] || response.headers["Location"];
    if (!locationHeader) throw new Error("Missing Location header from createOtherScenario response");

    return {
      ...otherScenario,
      id: extractIdFromLocation(locationHeader),
    } as OtherScenarioType;
  } catch (error: any) {
    return rejectWithValue(ExtractErrorInfo(error));
  }
});

export const updateOtherScenario = createAsyncThunk<
  OtherScenarioType,
  { incidentId: string; otherScenario: OtherScenarioType }
>("otherScenario/updateOtherScenario", async ({ incidentId, otherScenario }, { rejectWithValue }) => {
  try {
    const response = await api.patch<OtherScenarioType>(
      API_ENDPOINTS.updateOtherScenario(incidentId, otherScenario.id!),
      otherScenario
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(ExtractErrorInfo(error));
  }
});

const scenarioSlice = createSlice({
  name: "scenario",
  initialState,
  reducers: {
    clearForm: (state) => {
      state.vorScenario = initialState.vorScenario;
      state.otherScenario = initialState.otherScenario;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE vorScenario
      .addCase(createVorScenario.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVorScenario.fulfilled, (state, action) => {
        state.loading = false;
        state.vorScenario = action.payload;
      })
      .addCase(createVorScenario.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ErrorType;
      })
      // UPDATE vorScenario
      .addCase(updateVorScenario.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVorScenario.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateVorScenario.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ErrorType;
      })
      //CREATE otherScenario
      .addCase(createOtherScenario.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOtherScenario.fulfilled, (state, action) => {
        state.loading = false;
        state.otherScenario = action.payload;
      })
      .addCase(createOtherScenario.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ErrorType;
      });
    // UPDATE otherScenario
    builder
      .addCase(updateOtherScenario.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOtherScenario.fulfilled, (state, action) => {
        state.loading = false;
        state.otherScenario = action.payload;
      })
      .addCase(updateOtherScenario.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ErrorType;
      });
  },
});

export const { clearForm, clearError } = scenarioSlice.actions;

export const actionRootState = (state: RootState) => state;
export default scenarioSlice.reducer;
